import React, { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Alert } from "react-bootstrap";
import { getSidebarPermissionSections } from "@/common/Sidebar/sidebarNavConfig";
import type { SidebarNavGroup } from "@/common/Sidebar/sidebarNavConfig";
import {
  PLATFORM_POWERS,
  getGroupAccessAggregate,
  type RolePermissionsConfig,
  updateGroupPermission,
  updatePagePermission,
  updatePlatformPower,
} from "./employeeRolePermissions";
import styles from "./RolePermissionsEditor.module.scss";

interface RolePermissionsEditorProps {
  value: RolePermissionsConfig;
  onChange: (next: RolePermissionsConfig) => void;
}

interface AccessToggleProps {
  id?: string;
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (checked: boolean) => void;
}

function AccessToggle({
  id,
  checked,
  indeterminate = false,
  disabled = false,
  ariaLabel,
  onChange,
}: AccessToggleProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      id={id}
      type="checkbox"
      className={styles.accessCheckbox}
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
    />
  );
}

interface PermissionGroupRowsProps {
  group: SidebarNavGroup;
  value: RolePermissionsConfig;
  onChange: (next: RolePermissionsConfig) => void;
}

function PermissionGroupRows({ group, value, onChange }: PermissionGroupRowsProps) {
  const [expanded, setExpanded] = useState(false);
  const aggregate = getGroupAccessAggregate(value, group.id);
  const hasPages = group.pages.length > 0;

  return (
    <>
      <tr className={styles.groupRow}>
        <td className={styles.expandCell}>
          <button
            type="button"
            className={styles.expandBtn}
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "Show"} pages in ${group.label}`}
            disabled={!hasPages}
          >
            <i className={`bi bi-chevron-${expanded ? "down" : "right"}`} aria-hidden="true" />
          </button>
        </td>
        <td className={styles.nameCell}>{group.label}</td>
        <td className={styles.accessCell}>
          <AccessToggle
            checked={aggregate.viewChecked}
            indeterminate={aggregate.viewIndeterminate}
            ariaLabel={`${group.label} view`}
            onChange={(checked) =>
              onChange(updateGroupPermission(value, group.id, "view", checked))
            }
          />
        </td>
        <td className={styles.accessCell}>
          <AccessToggle
            checked={aggregate.editChecked}
            indeterminate={aggregate.editIndeterminate}
            disabled={!aggregate.viewChecked && !aggregate.viewIndeterminate}
            ariaLabel={`${group.label} edit`}
            onChange={(checked) =>
              onChange(updateGroupPermission(value, group.id, "edit", checked))
            }
          />
        </td>
      </tr>

      {expanded &&
        hasPages &&
        group.pages.map((page) => {
          const access = value.pagePermissions[page.id] || { view: false, edit: false };
          return (
            <tr key={page.id} className={styles.pageRow}>
              <td className={styles.expandCell} />
              <td className={styles.nameCell}>{page.label}</td>
              <td className={styles.accessCell}>
                <AccessToggle
                  checked={access.view}
                  ariaLabel={`${page.label} view`}
                  onChange={(checked) =>
                    onChange(updatePagePermission(value, page.id, "view", checked))
                  }
                />
              </td>
              <td className={styles.accessCell}>
                <AccessToggle
                  checked={access.edit}
                  disabled={!access.view}
                  ariaLabel={`${page.label} edit`}
                  onChange={(checked) =>
                    onChange(updatePagePermission(value, page.id, "edit", checked))
                  }
                />
              </td>
            </tr>
          );
        })}
    </>
  );
}

const RolePermissionsEditor: React.FC<RolePermissionsEditorProps> = ({
  value,
  onChange,
}) => {
  if (value.isFullAccess) {
    return (
      <Alert variant="info" className={styles.fullAccessAlert}>
        <strong>Full platform access.</strong> Super Admin can view and edit every page.
        Individual page permissions do not apply.
      </Alert>
    );
  }

  return (
    <div className={styles.editor}>
      <div className={styles.sectionHeader}>
        <h6 className={styles.sectionTitle}>Page Permissions</h6>
        <p className={styles.sectionHelp}>
          Set View/Edit on a group to apply to all its pages. Expand a group to adjust
          individual pages.
        </p>
      </div>

      <table className={styles.permissionTable}>
        <colgroup>
          <col className={styles.expandCol} />
          <col className={styles.nameCol} />
          <col className={styles.viewCol} />
          <col className={styles.editCol} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" aria-hidden="true" className={styles.expandCell} />
            <th scope="col" className={styles.nameCell} />
            <th scope="col" className={styles.accessHead}>
              View
            </th>
            <th scope="col" className={styles.accessHead}>
              Edit
            </th>
          </tr>
        </thead>
        <tbody>
          {getSidebarPermissionSections().map((group) => (
            <PermissionGroupRows
              key={group.id}
              group={group}
              value={value}
              onChange={onChange}
            />
          ))}
        </tbody>
      </table>

      <div className={styles.platformPowersSection}>
        <div className={styles.sectionHeader}>
          <h6 id="platform-powers-heading" className={styles.sectionTitle}>
            Platform Powers
          </h6>
          <p className={styles.sectionHelp}>
            Platform Powers are elevated capabilities and are separate from page access
            permissions.
          </p>
        </div>

        <table className={styles.permissionTable} aria-labelledby="platform-powers-heading">
          <colgroup>
            <col className={styles.expandCol} />
            <col className={styles.nameCol} />
            <col className={styles.viewCol} />
            <col className={styles.editCol} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" aria-hidden="true" className={styles.expandCell} />
              <th scope="col" className={styles.nameCell} />
              <th scope="col" colSpan={2} className={styles.accessHead}>
                Access
              </th>
            </tr>
          </thead>
          <tbody>
            {PLATFORM_POWERS.map((permission) => (
              <tr key={permission.id} className={styles.platformPowerRow}>
                <td className={styles.expandCell} aria-hidden="true" />
                <td className={styles.nameCell}>{permission.label}</td>
                <td colSpan={2} className={styles.accessCell}>
                  <AccessToggle
                    checked={value.platformPowers[permission.id] === true}
                    ariaLabel={permission.label}
                    onChange={(checked) =>
                      onChange(updatePlatformPower(value, permission.id, checked))
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RolePermissionsEditor;
